/** Batched adapter for the authored frontier kit. Uses the existing asset
 * compiler; repeated roof tiles and beams share geometry/material draws. */
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { compileAsset, type AssetMaterial, type CompiledInstance, type CompiledAsset, type ProceduralAssetV1 } from '@hyperlight/shared';
import { canopyPoint, type CanopyPlan, type FrontierTownPlan, type TownPlacement } from '../../../../shared/src/buildings/frontierTown';
import { addStylizedShading } from '../stylizedShading';
import {proceduralLathe} from '../../assets/proceduralLathe';
export function frontierTownView(town: FrontierTownPlan,options:{includeInstance?:(place:TownPlacement,instance:CompiledInstance)=>boolean;inspectableIds?:ReadonlySet<string>;transformForPlace?:(place:TownPlacement,out:THREE.Matrix4)=>void;compile?:(asset:ProceduralAssetV1)=>CompiledAsset}={}) {
    const group = new THREE.Group();
    const clothParts=new Map<string,{owner:string;material:AssetMaterial;parts:THREE.BufferGeometry[]}>();
    const placeMaterials=new Map<string,THREE.MeshStandardMaterial[]>();
    const batches = new Map<string, {
        owner:string;
        geometry: THREE.BufferGeometry;
        material: AssetMaterial;
        surface: 'wood' | 'metal' | 'stone' | 'dirt' | null;
        matrices: THREE.Matrix4[];
    }>();
    const parent = new THREE.Matrix4(), local = new THREE.Matrix4(), scale = new THREE.Matrix4();
    for (const place of town.places) {
        parent.makeRotationY(place.quarterTurn * Math.PI / 2);
        parent.setPosition(...place.position);
        options.transformForPlace?.(place,parent);
        const owner=(place.plan.cottage||place.plan.workplace)&&(!options.inspectableIds||options.inspectableIds.has(place.id))?place.id:'town';
        for(const canopy of place.plan.canopies){
            const material=place.plan.asset.materials[canopy.material],key=`${owner}:${JSON.stringify(material)}`;
            const cloth=clothParts.get(key)??{owner,material,parts:[]};clothParts.set(key,cloth);
            cloth.parts.push(canopyGeometry(canopy).applyMatrix4(parent));
        }
        for(const asset of [place.plan.asset,...place.plan.assetParts??[]])for (const inst of (options.compile??compileAsset)(asset).instances) {
            if(options.includeInstance&&!options.includeInstance(place,inst))continue;
            if (inst.deforms?.bend)
                throw new Error('Frontier kit adapter does not support bend');
            const mat = asset.materials[inst.material];
            const surface = [6, 8, 9, 10, 11, 13, 15].includes(inst.material) ? null
                : inst.material === 1 || inst.material === 12 ? 'wood'
                : inst.material === 7 ? 'metal' : inst.material === 14 ? 'dirt' : 'stone';
            // Radius/height belong in each instance transform. Only a cylinder's
            // top-to-bottom ratio changes its shape; stock and crop sizes can
            // otherwise share a draw just like differently sized timber boxes.
            const shape=inst.kind==='cylinder'?String(inst.params[0]/inst.params[1]):inst.kind==='lathe'?JSON.stringify([inst.params,inst.profile,inst.faceting??0]):'unit';
            const key = `${owner}:${surface}:${inst.kind}:${shape}:${JSON.stringify(inst.deforms ?? {})}:${JSON.stringify(mat)}`;
            let batch = batches.get(key);
            if (!batch) {
                batch = { owner, geometry: primitive(inst), material: mat, surface, matrices: [] };
                batches.set(key, batch);
            }
            local.fromArray(inst.transform);
            if (inst.kind === 'box')
                local.multiply(scale.makeScale(inst.params[0], inst.params[1], inst.params[2]));
            else if(inst.kind==='sphere')local.multiply(scale.makeScale(inst.params[0],inst.params[0],inst.params[0]));
            else if(inst.kind==='cone')local.multiply(scale.makeScale(inst.params[0],inst.params[1],inst.params[0]));
            else if(inst.kind==='cylinder')local.multiply(scale.makeScale(inst.params[1],inst.params[2],inst.params[1]));
            batch.matrices.push(parent.clone().multiply(local));
        }
    }
    // Small mixed prop groups cost more draws than geometry. Bake those by
    // owner/material, while keeping large tile/board/leaf arrays instanced.
    const grouped=new Map<string,Array<(typeof batches extends Map<string,infer B>?B:never)>>();
    for(const batch of batches.values()){
        const key=`${batch.owner}:${batch.surface}:${JSON.stringify(batch.material)}`;
        const list=grouped.get(key)??[];list.push(batch);grouped.set(key,list);
    }
    const renderBatches:Array<(typeof batches extends Map<string,infer B>?B:never)>=[];
    for(const list of grouped.values()){
        if(list.length===1||list.reduce((sum,b)=>sum+b.matrices.length,0)>128){renderBatches.push(...list);continue;}
        const pieces:THREE.BufferGeometry[]=[];
        for(const b of list)for(const matrix of b.matrices){
            const copy=b.geometry.clone().applyMatrix4(matrix);
            if(copy.index){pieces.push(copy.toNonIndexed());copy.dispose();}else pieces.push(copy);
        }
        const geometry=mergeGeometries(pieces);pieces.forEach(g=>g.dispose());
        if(!geometry){renderBatches.push(...list);continue;}
        list.forEach(b=>b.geometry.dispose());
        renderBatches.push({...list[0],geometry,matrices:[new THREE.Matrix4()]});
    }
    const materialCache = new Map<string, THREE.MeshStandardMaterial>();
    for (const batch of renderBatches) {
        const key = `${batch.owner}:${JSON.stringify(batch.material)}`;
        let material = materialCache.get(key);
        if (!material) {
            material = new THREE.MeshStandardMaterial(batch.material);
            addStylizedShading(material, { weatherGround: true });
            materialCache.set(key, material);
            const owned=placeMaterials.get(batch.owner)??[];owned.push(material);placeMaterials.set(batch.owner,owned);
        }
        const mesh = new THREE.InstancedMesh(batch.geometry, material, batch.matrices.length);
        mesh.userData.footstepSurface = batch.surface;
        batch.matrices.forEach((m, i) => mesh.setMatrixAt(i, m));
        mesh.castShadow = mesh.receiveShadow = true;
        mesh.computeBoundingSphere();
        group.add(mesh);
    }
    const clothMaterials=new Map<string,THREE.MeshStandardMaterial>();
    const clothGeometries:THREE.BufferGeometry[]=[];const clothMeshes=new Map<string,THREE.Mesh[]>();
    for(const {owner,material,parts} of clothParts.values()){
        if(!parts.length)continue;
        const geometry=mergeGeometries([...parts]);parts.forEach(g=>g.dispose());if(!geometry)continue;
        const key=JSON.stringify(material);let linen=clothMaterials.get(key);
        if(!linen){linen=new THREE.MeshStandardMaterial({...material,side:THREE.DoubleSide});clothMaterials.set(key,linen);}
        clothGeometries.push(geometry);const mesh=new THREE.Mesh(geometry,linen);mesh.castShadow=mesh.receiveShadow=true;group.add(mesh);
        const owned=clothMeshes.get(owner)??[];owned.push(mesh);clothMeshes.set(owner,owned);
    }
    const sections=new Map<string,THREE.Plane>();
    const setCutaway=(id:string,height:number|null)=>{
            const section=sections.get(id)??new THREE.Plane(new THREE.Vector3(0,-1,0),0);sections.set(id,section);
            if(height!==null)section.constant=height;
            for(const m of placeMaterials.get(id)??[]){m.clippingPlanes=height===null?null:[section];m.clipShadows=true;m.needsUpdate=true;}
            for(const cloth of clothMeshes.get(id)??[])cloth.visible=height===null;
    };
    return { group, batches:renderBatches.length+clothGeometries.length,setCutaway,
        setCottageCutaway(height:number|null){setCutaway('home',height);},
        dispose() { group.traverse(o => { if (o instanceof THREE.InstancedMesh)o.dispose(); });
            clothGeometries.forEach(g=>g.dispose());for(const m of clothMaterials.values())m.dispose();for(const b of renderBatches)b.geometry.dispose();for(const m of materialCache.values())m.dispose();group.removeFromParent(); }
    };
}
/** The cloth and frame use identical boundary coordinates, including bay seams. */
export function canopyGeometry(c: CanopyPlan): THREE.BufferGeometry {
    const positions: number[] = [], uv: number[] = [], indices: number[] = [], nx = 12, nz = 12;
    for (let z = 0; z <= nz; z++)
        for (let x = 0; x <= nx; x++) {
            positions.push(...canopyPoint(c, x / nx, z / nz));
            uv.push(x / nx, z / nz);
        }
    for (let z = 0; z < nz; z++)
        for (let x = 0; x < nx; x++) {
            const a = z * (nx + 1) + x, b = a + 1, d = a + nx + 1, e = d + 1;
            indices.push(a, d, b, b, d, e);
        }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(indices);
    g.computeVertexNormals();
    return g;
}
function primitive(i: CompiledInstance): THREE.BufferGeometry {
    const p = i.params;
    let geometry: THREE.BufferGeometry;
    switch (i.kind) {
        case 'box':
            geometry = new THREE.BoxGeometry(1, 1, 1);
            break;
        case 'cylinder':
            geometry = new THREE.CylinderGeometry(p[0]/p[1], 1, 1, 10);
            break;
        case 'cone':
            geometry = new THREE.ConeGeometry(1, 1, 6);
            break;
        case 'lathe':
            geometry = proceduralLathe(i);
            if(geometry.index){const flat=geometry.toNonIndexed();geometry.dispose();geometry=flat;}
            geometry.computeVertexNormals();
            break;
        case 'sphere':
            geometry = new THREE.IcosahedronGeometry(1, 0);
            break;
        default: throw new Error(`Unsupported frontier primitive: ${i.kind}`);
    }
    if (i.deforms?.taper) {
        const taper = i.deforms.taper, axis = { x: 0, y: 1, z: 2 }[taper.axis];
        geometry.computeBoundingBox();
        const bounds = geometry.boundingBox!, lo = bounds.min.getComponent(axis), span = bounds.max.getComponent(axis) - lo;
        const pos = geometry.getAttribute('position'), v = new THREE.Vector3();
        for (let j = 0; j < pos.count; j++) {
            v.fromBufferAttribute(pos, j);
            const k = THREE.MathUtils.lerp(1, taper.factor, (v.getComponent(axis) - lo) / Math.max(span, 1e-6));
            for (let a = 0; a < 3; a++)
                if (a !== axis)
                    v.setComponent(a, v.getComponent(a) * k);
            pos.setXYZ(j, v.x, v.y, v.z);
        }
        geometry.computeVertexNormals();
    }
    return geometry;
}
